import React, { useState, useRef, useCallback } from 'react';
import type { TilePosition } from '../../types/dashboard';

interface DraggableTileProps {
  children: React.ReactNode;
  position: TilePosition;
  onDragStart: (tileId: string) => void;
  onDragEnd: (tileId: string, clientX: number, clientY: number) => void;
  onDragMove: (clientX: number, clientY: number) => void;
  isBeingDragged: boolean;
  isMovementEnabled: boolean;
}

export const DraggableTile: React.FC<DraggableTileProps> = ({
  children,
  position,
  onDragStart,
  onDragEnd,
  onDragMove,
  isBeingDragged,
  isMovementEnabled
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 });
  const tileRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    
    if (!isMovementEnabled || !tileRef.current) return;
    
    const rect = tileRef.current.getBoundingClientRect();
    const offset = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    
    // Capture original size before switching to fixed positioning
    setOriginalSize({
      width: rect.width,
      height: rect.height
    });
    
    setDragOffset(offset);
    
    // Initialize drag position to current tile position to avoid jumping
    setDragPosition({ 
      x: rect.left, 
      y: rect.top 
    });
    
    setIsDragging(true);
    onDragStart(position.id);
    
    
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    // Calculate position relative to viewport for fixed positioning
    const newPosition = {
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    };
    
    setDragPosition(newPosition);
    onDragMove(e.clientX, e.clientY);
  }, [isDragging, dragOffset.x, dragOffset.y, onDragMove]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    onDragEnd(position.id, e.clientX, e.clientY);
  }, [isDragging, onDragEnd, position.id]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const tileStyle: React.CSSProperties = {
    gridColumn: isBeingDragged ? 'unset' : `${position.x + 1} / span ${position.width}`,
    gridRow: isBeingDragged ? 'unset' : `${position.y + 1} / span ${position.height}`,
    cursor: isMovementEnabled ? (isBeingDragged ? 'grabbing' : 'grab') : 'default',
    opacity: isBeingDragged ? 0.8 : 1,
    transform: isBeingDragged 
      ? 'scale(1.02) rotate(1deg)' 
      : 'scale(1)',
    transition: isBeingDragged ? 'none' : 'all 0.2s ease',
    zIndex: isBeingDragged ? 1000 : 1,
    userSelect: 'none',
    position: isBeingDragged ? 'fixed' : 'relative',
    top: isBeingDragged ? `${dragPosition.y}px` : 'auto',
    left: isBeingDragged ? `${dragPosition.x}px` : 'auto',
    width: isBeingDragged ? `${originalSize.width}px` : 'auto',
    height: isBeingDragged ? `${originalSize.height}px` : 'auto',
    pointerEvents: isBeingDragged ? 'none' : 'auto'
  };

  return (
    <div
      ref={tileRef}
      style={tileStyle}
      onMouseDown={handleMouseDown}
      className={`draggable-tile ${isBeingDragged ? 'dragging' : ''}`}
    >
      {children}
    </div>
  );
};